import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const ImageManager = ({ salonId, onImagesChange }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, imageIndex: null, title: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, imageIndex: null });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  console.log('ImageManager rendered with salonId:', salonId);

  useEffect(() => {
    console.log('useEffect triggered with salonId:', salonId);
    if (salonId) {
      fetchImages();
    }
  }, [salonId]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      console.log('Fetching images for salon ID:', salonId);
      console.log('API Base URL:', process.env.REACT_APP_API_URL || 'https://glamapp.api.webspeed.online/api');
      console.log('Full API URL:', `${process.env.REACT_APP_API_URL || 'https://glamapp.api.webspeed.online/api'}/admin/salons/${salonId}/images`);
      
      const response = await adminAPI.getSalonImages(salonId);
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      
      // Backend returns: { success: true, data: { images: [...], totalImages: number } }
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    try {
      setUploading(true);
      setError('');
      
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('images', file);
      });

      const response = await adminAPI.uploadSalonImages(salonId, formData);
      console.log('Upload response:', response);
      
      // Backend returns: { success: true, message: "X images uploaded successfully", data: { uploadedImages, failedUploads, totalImages } }
      const uploadedCount = response.data.uploadedImages?.length || 0;
      setSuccess(`${uploadedCount} images uploaded successfully`);
      setUploadDialog(false);
      setSelectedFiles([]);
      await fetchImages();
      
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageIndex) => {
    try {
      setError('');
      await adminAPI.deleteSalonImage(salonId, imageIndex);
      setSuccess('Image deleted successfully');
      setDeleteDialog({ open: false, imageIndex: null });
      await fetchImages();
      
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.response?.data?.message || 'Failed to delete image');
    }
  };

  const handleSetPrimary = async (imageIndex) => {
    try {
      setError('');
      await adminAPI.setPrimarySalonImage(salonId, imageIndex);
      setSuccess('Primary image updated successfully');
      await fetchImages();
      
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
      setError(error.response?.data?.message || 'Failed to set primary image');
    }
  };

  const handleEditTitle = async () => {
    try {
      setError('');
      await adminAPI.updateSalonImageTitle(salonId, editDialog.imageIndex, editDialog.title);
      setSuccess('Image title updated successfully');
      setEditDialog({ open: false, imageIndex: null, title: '' });
      await fetchImages();
    } catch (error) {
      console.error('Error updating image title:', error);
      setError(error.response?.data?.message || 'Failed to update image title');
    }
  };

  const openEditDialog = (imageIndex, currentTitle) => {
    setEditDialog({
      open: true,
      imageIndex,
      title: currentTitle || ''
    });
  };

  const openDeleteDialog = (imageIndex) => {
    setDeleteDialog({
      open: true,
      imageIndex
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Image Management ({images.length} images)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialog(true)}
        >
          Add Images
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Images Grid */}
      {images.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="textSecondary">
            No images uploaded yet
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialog(true)}
            sx={{ mt: 2 }}
          >
            Upload First Image
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Box position="relative">
                  <CardMedia
                    component="img"
                    height="200"
                    image={image.url}
                    alt={image.title || `Salon image ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  {image.isPrimary && (
                    <Chip
                      label="Primary"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(25, 118, 210, 0.9)'
                      }}
                    />
                  )}
                </Box>
                
                <Box p={1}>
                  <Typography variant="body2" noWrap>
                    {image.title || `Image ${index + 1}`}
                  </Typography>
                </Box>

                <CardActions sx={{ justifyContent: 'space-between', px: 1, pb: 1 }}>
                  <Box>
                    <Tooltip title={image.isPrimary ? "Primary image" : "Set as primary"}>
                      <IconButton
                        size="small"
                        onClick={() => !image.isPrimary && handleSetPrimary(index)}
                        disabled={image.isPrimary}
                        color={image.isPrimary ? "primary" : "default"}
                      >
                        {image.isPrimary ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Edit title">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(index, image.title)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Tooltip title="Delete image">
                    <IconButton
                      size="small"
                      onClick={() => openDeleteDialog(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Images</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="image-upload-input"
            />
            <label htmlFor="image-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Images (Max 10)
              </Button>
            </label>
            
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                {selectedFiles.map((file, index) => (
                  <Typography key={index} variant="body2" color="textSecondary">
                    • {file.name}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || selectedFiles.length === 0}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Title Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, imageIndex: null, title: '' })}>
        <DialogTitle>Edit Image Title</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image Title"
            fullWidth
            variant="outlined"
            value={editDialog.title}
            onChange={(e) => setEditDialog({ ...editDialog, title: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, imageIndex: null, title: '' })}>
            Cancel
          </Button>
          <Button onClick={handleEditTitle} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, imageIndex: null })}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, imageIndex: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteDialog.imageIndex)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageManager;